import React, { useState } from "react";
import { Link } from "react-router-dom";
import { FiChevronDown, FiChevronRight } from "react-icons/fi";
import HelpLayout from "./HelpLayout";

const faqs = [
  {
    q: "Is my content private?",
    a: (
      <>
        Yes. Your bits, sets, and journal entries are yours — JokeLab claims
        no ownership over what you write, and you're not giving up any
        rights by using it. We will never sell any user data. The only
        third parties that ever see your content are ones JokeLab uses to
        power a feature you choose to use, and only if they won't retain or
        use the data.
      </>
    ),
  },
  {
    q: "Who is Grr?",
    a: (
      <>
        Grr is JokeLab's mascot — the dog in the logo. His full name is
        Jokgrr, and the{" "}
        <Link to="/help/overview?section=grrr-menu" className="text-blue-600 font-semibold hover:underline">
          Grrr menu
        </Link>
        's tools (Add Bits, Find Laughs, and{" "}
        <Link to="/help/overview?section=grr-review" className="text-blue-600 font-semibold hover:underline">
          Grr Review
        </Link>
        ) are named after him.
      </>
    ),
  },
  {
    q: "Is Grr AI?",
    a: (
      <>
        Yes, but all AI can be disabled in{" "}
        <Link to="/help/overview?section=settings" className="text-blue-600 font-semibold hover:underline">
          Settings
        </Link>
        .
      </>
    ),
  },
  {
    q: "Can I turn off any AI?",
    a: (
      <>
        Yes — in{" "}
        <Link to="/help/overview?section=settings" className="text-blue-600 font-semibold hover:underline">
          Settings
        </Link>{" "}
        (click your name in the header), check <b>Disable AI</b> to hide the{" "}
        <Link to="/help/overview?section=grrr-menu" className="text-blue-600 font-semibold hover:underline">
          Grrr menu
        </Link>{" "}
        and question box in the editor.
      </>
    ),
  },
  {
    q: "How is Grrr used in JokeLab?",
    a: (
      <>
        Grrr doesn't write material for you. It powers{" "}
        <Link to="/help/overview?section=grrr-menu" className="text-blue-600 font-semibold hover:underline">
          Add Bits and Find Laughs
        </Link>
        , which help import and edit text you've already written, plus{" "}
        <Link to="/help/overview?section=grr-review" className="text-blue-600 font-semibold hover:underline">
          Grr Review
        </Link>
        , which gives a quick critique of it. There's also a question box for
        asking about your set.
      </>
    ),
  },
  {
    q: "What happens to my content when I use a Grrr tool?",
    a: (
      <>
        Only the document text you're working on (or your question) is sent
        when you use one of these tools — it doesn't run automatically in the
        background. JokeLab doesn't separately store your prompts or the
        replies; that text is sent to an AI provider to generate the
        response, and is never used to train any AI model.
      </>
    ),
  },
  {
    q: "Does JokeLab cost anything?",
    a: <>No — and it will stay that way as long as we can help it.</>,
  },
  {
    q: "How is my content timed?",
    a: (
      <>
        From your words-per-minute pace, set in{" "}
        <Link to="/help/overview?section=settings" className="text-blue-600 font-semibold hover:underline">
          Settings
        </Link>{" "}
        (click your name in the header). Word counts in your document — minus
        stage directions in <code>[brackets]</code>, which don't count — are
        converted to time using that pace, giving each bit's start/end time
        in{" "}
        <Link to="/help/overview?section=modes" className="text-blue-600 font-semibold hover:underline">
          Perform mode
        </Link>{" "}
        and the total time shown there and in your stats on the index page.
        It defaults to 120 words per minute until you change it.
      </>
    ),
  },
  {
    q: "Where can I get more help?",
    a: (
      <>
        JokeLab's Discord is at{" "}
        <a
          href="https://discord.gg/xjMRmBVAQv"
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 font-semibold hover:underline"
        >
          discord.gg/xjMRmBVAQv
        </a>
        . Use it for questions or feedback.
      </>
    ),
  },
];

function HelpPage() {
  const [openIndexes, setOpenIndexes] = useState(() => new Set());

  const toggle = (index) => {
    setOpenIndexes((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  return (
    <HelpLayout title="JokeLab Help">
      <Link
        to="/help/overview"
        className="block bg-white rounded-lg shadow-sm p-4 mb-6 hover:shadow-md transition"
      >
        <div className="text-base font-bold text-green-900">Feature overview &rarr;</div>
        <div className="text-sm text-gray-600 mt-1">
          A tour of documents, bits, modes, the Grrr menu, journal, and settings.
        </div>
      </Link>

      <h2 className="text-base font-bold text-gray-500 uppercase tracking-wide mb-3">
        FAQ
      </h2>
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        {faqs.map((faq, index) => {
          const open = openIndexes.has(index);
          return (
            <div
              key={faq.q}
              className={index > 0 ? "border-t border-gray-100" : ""}
            >
              <button
                type="button"
                onClick={() => toggle(index)}
                className="w-full flex items-center justify-between text-left px-5 py-4 hover:bg-gray-50 transition"
                aria-expanded={open}
              >
                <span className="text-sm font-semibold text-gray-900">{faq.q}</span>
                {open ? (
                  <FiChevronDown size={16} className="text-gray-400 flex-shrink-0 ml-3" />
                ) : (
                  <FiChevronRight size={16} className="text-gray-400 flex-shrink-0 ml-3" />
                )}
              </button>
              {open && (
                <div className="px-5 pb-4 text-sm text-gray-700 leading-relaxed">
                  {faq.a}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </HelpLayout>
  );
}

export default HelpPage;
